import React, { useState } from "react";
import {
  Formik,
  FormikActions,
  Form as FormikForm,
  Field,
  FormikProps,
} from "formik";
import { InjectedIntlProps, injectIntl, FormattedMessage } from "react-intl";
import TextInput from "../../text-input";
import InputErrorMessage from "../../input-error-message";

import messages from "./EditPlayerForm.intl";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../../types/styled-arguments";
import { required } from "../../form-utils";
import { POSITION_LABELS } from "../../../utils/positions";

import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import moment from "moment";

const StyledForm = styled(FormikForm)({
  display: "flex",
  flexDirection: "column",
});

const IdentityHeader = styled("div")(({ theme }: IStyledArguments) => ({
  textAlign: "center",
  marginBottom: theme.spacing(2.75),
}));

const AvatarWrap = styled("div")({
  position: "relative",
  width: 72,
  height: 72,
  margin: "0 auto",
});

const Avatar = styled("div")(({ theme }: IStyledArguments) => ({
  width: 72,
  height: 72,
  borderRadius: "50%",
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.4rem",
  color: theme.palette.primary.dark,
}));

const EditBadge = styled("div")(({ theme }: IStyledArguments) => ({
  position: "absolute",
  right: -2,
  bottom: -2,
  width: 26,
  height: 26,
  borderRadius: "50%",
  background: theme.palette.background.paper,
  border: `1.5px solid ${theme.palette.extra.borderColor}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    color: theme.palette.primary.dark,
  },
}));

const IdentityTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.15rem",
  color: theme.palette.text.primary,
  marginTop: theme.spacing(1.5),
}));

const SectionCard = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 20,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  padding: theme.spacing(2.75),
  marginBottom: theme.spacing(1.75),
}));

const SectionTitle = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.75rem",
  color: theme.palette.primary.dark,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: theme.spacing(1.5),
}));

const FieldsColumn = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  "& > *": {
    marginTop: 0,
    marginBottom: 0,
  },
}));

const FieldsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  "& > *": {
    flex: 1,
    minWidth: 0,
  },
}));

const PositionLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.72rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  marginBottom: theme.spacing(1),
}));

const PositionScroll = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1),
  overflowX: "auto",
  paddingBottom: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  minWidth: 0,
}));

const PositionChip = styled("button")<{ $active?: boolean }>(
  ({ theme, $active }: IStyledArguments & { $active?: boolean }) => ({
    flexShrink: 0,
    appearance: "none",
    cursor: "pointer",
    border: "none",
    borderRadius: 20,
    padding: theme.spacing(0.9, 1.75),
    fontFamily: theme.typography.fontFamily,
    fontSize: "0.78rem",
    fontWeight: 600,
    background: $active ? theme.palette.primary.main : theme.palette.extra.backgroundGrey,
    color: $active ? theme.palette.primary.contrastText : theme.palette.text.secondary,
  })
);

const SaveButton = styled("button")(({ theme }: IStyledArguments) => ({
  width: "100%",
  boxSizing: "border-box",
  background: theme.palette.primary.main,
  border: "none",
  color: theme.palette.primary.contrastText,
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.9rem",
  borderRadius: 10,
  padding: theme.spacing(1.6, 0),
  cursor: "pointer",
  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));

const getInitials = (name: string, surname: string) =>
  `${(name && name[0]) || ""}${(surname && surname[0]) || ""}`.toUpperCase();

export interface IFormValues {
  name: string;
  surname: string;
  nickname?: string;
  number?: number;
  position: string;
  dateOfBirth: string;
}

interface IForm extends InjectedIntlProps {
  onSubmit: (arg: IFormValues) => void;
  checkValid: boolean;
  name: string;
  surname: string;
  nickname?: string;
  number?: number;
  position: string;
  dateOfBirth: string;
}

const EditPlayerForm = ({
  onSubmit,
  checkValid,
  name,
  surname,
  number,
  position,
  nickname,
  dateOfBirth,
  intl,
}: IForm) => {
  const date = new Date(Number(dateOfBirth));
  const formattedDate =
    date.getFullYear() +
    "-" +
    ("0" + (date.getMonth() + 1)).slice(-2) +
    "-" +
    ("0" + date.getDate()).slice(-2);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  return (
    <Formik
      initialValues={{
        name,
        surname,
        number,
        position,
        nickname,
        dateOfBirth: formattedDate,
      }}
      isInitialValid={checkValid}
      onSubmit={(values: IFormValues, actions: FormikActions<IFormValues>) => {
        onSubmit(values);
        actions.setSubmitting(false);
      }}
      render={({
        errors,
        touched,
        values,
        isValid,
        setFieldValue,
      }: FormikProps<IFormValues>) => (
        <>
          <IdentityHeader>
            <AvatarWrap>
              <Avatar>{getInitials(name, surname)}</Avatar>
              <EditBadge>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                </svg>
              </EditBadge>
            </AvatarWrap>
            <IdentityTitle>
              <FormattedMessage {...messages.registerTitle} /> {name} {surname}
            </IdentityTitle>
          </IdentityHeader>

          <StyledForm data-testid="add-player-form">
            <SectionCard>
              <SectionTitle>
                <FormattedMessage {...messages.identitySection} />
              </SectionTitle>
              <FieldsColumn>
                <Field
                  name="name"
                  validate={required(intl)}
                  error={errors.name && touched.name}
                  component={TextInput}
                  fullWidth
                  inputProps={{
                    "data-testid": "name",
                  }}
                  label={intl.formatMessage(messages.name)}
                />
                <InputErrorMessage name="name" />
                <Field
                  name="surname"
                  validate={required(intl)}
                  error={errors.surname && touched.surname}
                  component={TextInput}
                  fullWidth
                  inputProps={{
                    "data-testid": "surname",
                  }}
                  label={intl.formatMessage(messages.surname)}
                />
                <InputErrorMessage name="surname" />
              </FieldsColumn>
            </SectionCard>

            <SectionCard>
              <SectionTitle>
                <FormattedMessage {...messages.detailsSection} />
              </SectionTitle>

              <PositionLabel>
                <FormattedMessage {...messages.position} />
              </PositionLabel>
              <PositionScroll>
                {Object.keys(POSITION_LABELS).map((key) => (
                  <PositionChip
                    key={key}
                    type="button"
                    $active={values.position === key}
                    onClick={() => setFieldValue("position", key)}
                  >
                    {POSITION_LABELS[key]}
                  </PositionChip>
                ))}
              </PositionScroll>

              <FieldsRow>
                <Field
                  name="number"
                  validate={required(intl)}
                  error={errors.number && touched.number}
                  component={TextInput}
                  fullWidth
                  inputProps={{
                    "data-testid": "number",
                  }}
                  label={intl.formatMessage(messages.number)}
                />

                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    fullWidth
                    margin="none"
                    id="date-picker-dialog"
                    label="Date of birth"
                    format="yyyy-MM-dd"
                    value={selectedDate}
                    onChange={(date: any) => {
                      handleDateChange(date);
                      setFieldValue("dateOfBirth", moment(date).format("YYYY-MM-DD"));
                    }}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                  />
                </MuiPickersUtilsProvider>
              </FieldsRow>
              <InputErrorMessage name="number" />
            </SectionCard>

            <SaveButton type="submit" data-testid="go-next-button" disabled={!isValid}>
              <FormattedMessage {...messages.save} />
            </SaveButton>
          </StyledForm>
        </>
      )}
    />
  );
};

export default injectIntl(EditPlayerForm);
